import moment from "moment";
import { Journey, Leg, MyBookingsJourneys, Segment } from "../global";

export const convertSecIntoTime = (timeInSec: number) => {
  const hours = Math.floor(timeInSec / 3600);
  const minutes = Math.floor((timeInSec % 3600) / 60);
  return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
};

export const calculateJourneyDuration = (journey: Journey): moment.Duration => {
  if (!journey.segments) return moment.duration(0);
  const lastSegmentIndex = journey.segments.length - 1;
  const lastLegIndex = journey.segments[lastSegmentIndex].legs.length - 1;
  const arrivalTime = moment(journey?.segments[lastSegmentIndex]?.legs[lastLegIndex]?.legInfo?.arrivalTimeUtc);
  const departureTime = moment(journey?.segments[0]?.legs[0]?.legInfo?.departureTimeUtc);
  return moment.duration(arrivalTime?.diff(departureTime));
};

export const calculateMyBookingsJourneyDuration = (journey: MyBookingsJourneys): moment.Duration | null => {
  // Check if departure and arrival times are provided
  if (!journey || !journey.departureTimeUtc || !journey.arrivalTimeUtc) {
    return null; // Return null if any of the inputs are missing
  }

  // Attempt to create moment objects from departure and arrival times
  const arrivalTime = moment(journey.arrivalTimeUtc);
  const departureTime = moment(journey.departureTimeUtc);

  // Check if moment objects are valid
  if (!arrivalTime.isValid() || !departureTime.isValid()) {
    return null; // Return null if either of the moment objects is invalid
  }

  // Calculate and return the duration
  return moment.duration(arrivalTime.diff(departureTime));
};


export const calculateSegmentDuration = (segment: Segment): moment.Duration => {
  const lastLegIndex = segment?.legs?.length - 1;
  const arrivalTime = moment(segment?.legs[lastLegIndex]?.legInfo?.arrivalTimeUtc);
  const departureTime = moment(segment?.legs[0]?.legInfo?.departureTimeUtc);
  return moment.duration(arrivalTime?.diff(departureTime));
};

export const calculateLegDuration = (leg: Leg): moment.Duration => {
  const arrivalTime = moment(leg?.legInfo?.arrivalTimeUtc);
  const departureTime = moment(leg?.legInfo?.departureTimeUtc);
  return moment.duration(arrivalTime?.diff(departureTime));
};

export const calculateLegLayoverDuration = (first: Leg, second: Leg): moment.Duration => {
  const arrivalTime = moment(first?.legInfo?.arrivalTimeUtc);
  const departureTime = moment(second?.legInfo?.departureTimeUtc);
  return moment.duration(departureTime?.diff(arrivalTime));
};

export const calculateSegmentLayoverDuration = (first: Segment, second: Segment): moment.Duration => {
  const lastLegIndex = first.legs.length - 1;
  const arrivalTime = moment(first?.legs[lastLegIndex]?.legInfo?.arrivalTimeUtc);
  const departureTime = moment(second?.legs[0]?.legInfo?.departureTimeUtc);
  return moment.duration(departureTime?.diff(arrivalTime));
};

export const getDurationString = (duration: moment.Duration): String => {
  const hours = Math.floor(duration.asHours());
  const mins = duration.minutes();
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

export const handleDuration = (time: string) => {
  const normalizedTime = time.toLowerCase(); // Convert to lowercase for case insensitivity

  if (normalizedTime.includes("h")) {
    // If time includes "h", handle formats like "1h", "01:00"
    const [hours, minutes] = normalizedTime.split("h").map((part) => parseInt(part.trim(), 10));
    return `${hours.toString().padStart(2, "0")}:${minutes ? minutes.toString().padStart(2, "0") : "00"}`;
  }
  if (normalizedTime.includes("m")) {
    // If time includes "m", handle format like "30m" convert to "00:30"
    const minutes = parseInt(normalizedTime.split("m")[0].trim(), 10);
    return `00:${minutes.toString().padStart(2, "0")}`;
  }
  // If both hours and minutes provided like "1h 30m", convert to "01:30"
  const [hours, minutes] = normalizedTime.split(" ").map((part) => parseInt(part.trim(), 10));
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
};
